/**
 * organizeStyles — the dark-rail chrome for the organization surface
 * (2026-09-09): anchored menus in the SidebarAccountMenu idiom, ghost icon
 * buttons in the RoutineManageButton idiom, the inline rename input, the
 * in-list notice, and the drop-target look. Every colour is the rail's own
 * gold family; the menus ride BODY_PORTAL_Z_INDEX like every body portal.
 */
import styled from 'styled-components';
import { BODY_PORTAL_Z_INDEX } from './styles';

export const RailMenu = styled.div.attrs<{ $left: number; $top: number }>((props) => ({
  style: { left: `${props.$left}px`, top: `${props.$top}px` },
}))<{ $left: number; $top: number }>`
  position: fixed;
  z-index: ${BODY_PORTAL_Z_INDEX};
  min-width: 208px;
  max-width: min(280px, calc(100vw - 16px));
  padding: 6px;
  background: rgba(26, 26, 26, 0.98);
  border: 1px solid rgba(198, 167, 94, 0.35);
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(198, 167, 94, 0.12);
`;

export const RailMenuItem = styled.button<{ $danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: ${(props) => (props.$danger ? 'rgba(226, 120, 120, 0.95)' : 'var(--brand-gold)')};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 12.5px;
  font-weight: 500;
  letter-spacing: 0.02em;
  cursor: pointer;
  text-align: left;
  appearance: none;
  -webkit-appearance: none;

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
  &:hover:not(:disabled) {
    background: rgba(198, 167, 94, 0.1);
  }
  &:focus-visible {
    outline: 2px solid rgba(198, 167, 94, 0.6);
    outline-offset: -2px;
  }
  svg {
    flex: none;
    width: 14px;
    height: 14px;
  }
`;

export const RailMenuHeading = styled.div`
  padding: 6px 10px 4px;
  color: rgba(182, 146, 76, 0.75);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
`;

export const RailMenuInput = styled.input`
  width: 100%;
  margin: 2px 0 4px;
  padding: 7px 10px;
  border: 1px solid rgba(198, 167, 94, 0.35);
  border-radius: 6px;
  background: rgba(198, 167, 94, 0.06);
  color: var(--brand-gold-hover);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 12.5px;
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: rgba(182, 146, 76, 0.55);
  }
  &:focus {
    border-color: var(--brand-gold);
    box-shadow: 0 0 0 2px rgba(198, 167, 94, 0.2);
  }
`;

/**
 * A quiet ghost icon button for the rail (the RoutineManageButton language):
 * transparent at rest, revealed by the row / header it lives in, always
 * visible on touch, a real hit size.
 */
export const RailGhostIconButton = styled.button<{ $always?: boolean }>`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  margin: 0;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  color: rgba(182, 146, 76, 0.8);
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  opacity: ${(props) => (props.$always ? 1 : 0)};
  transition: opacity 0.12s ease, background 0.12s ease;

  &:hover {
    color: var(--brand-gold);
    background: rgba(198, 167, 94, 0.1);
  }
  &:focus-visible {
    opacity: 1;
    outline: 2px solid rgba(198, 167, 94, 0.6);
    outline-offset: 1px;
  }
  svg {
    width: 12px;
    height: 12px;
    display: block;
  }

  /* Touch: no hover exists — the affordance is always there, at a real size. */
  @media (hover: none) {
    opacity: 1;
    width: 28px;
    height: 28px;
  }
`;

/** The inline rename field a row's title swaps for (dark twin of ThreadHeaderRenameInput). */
export const RailInlineInput = styled.input`
  width: 100%;
  min-width: 0;
  padding: 2px 6px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: var(--brand-gold-hover);
  background: rgba(198, 167, 94, 0.06);
  border: 1px solid rgba(198, 167, 94, 0.35);
  border-radius: 4px;
  box-sizing: border-box;
  outline: none;

  &:focus {
    border-color: rgba(198, 167, 94, 0.6);
  }
`;

/** The in-list notice ("moved to clients · undo"), pinned under the list. */
export const RailNotice = styled.div<{ $placement?: 'flow' | 'toast' }>`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 8px 4px;
  ${(p) => p.$placement !== 'toast' ? '' : `
    /* TOAST (2026-09-20). In the rail this used to sit inside the scroller,
       AFTER every section — so it pushed the rows down as it appeared, and on
       a list longer than the rail it appeared below the fold, where the person
       who had just moved a thread could not see it or press undo. Pinned above
       the footer instead: it displaces nothing and is always where the eye is.
       The mobile sheet keeps the in-flow placement, which suits a short list
       and a surface with no footer to sit above. */
    position: absolute;
    left: 8px;
    right: 8px;
    bottom: 8px;
    margin: 0;
    z-index: 2;
    background: rgba(28, 26, 22, 0.98);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.45);
  `}
  padding: 8px 10px;
  background: rgba(198, 167, 94, 0.1);
  border: 1px solid rgba(198, 167, 94, 0.3);
  border-radius: 8px;
  color: var(--brand-gold-hover);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 12px;
  line-height: 1.3;
`;

export const RailNoticeAction = styled.button`
  margin-left: auto;
  padding: 2px 8px;
  background: transparent;
  border: 1px solid rgba(198, 167, 94, 0.45);
  border-radius: 6px;
  color: var(--brand-gold);
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;

  &:hover {
    background: rgba(198, 167, 94, 0.12);
  }
  &:focus-visible {
    outline: 2px solid rgba(198, 167, 94, 0.6);
    outline-offset: 1px;
  }
`;

/**
 * "load older threads" — the door BELOW the live window (2026-09-22).
 *
 * The rail shows only the newest threads by activity, and nothing re-stamps an
 * idle thread, so without this the rest of a person's history is unreachable
 * from the list. Deliberately quiet: it is the last row of the list, not a
 * call to action.
 *
 * `min-width`/`min-height` are declared alongside `height` because
 * GlobalStyles floors EVERY button at 44px and a declaration-free small button
 * silently renders at that size.
 */
export const RailLoadOlderButton = styled.button`
  display: block;
  width: calc(100% - 12px);
  margin: 2px 6px 6px;
  padding: var(--rail-row-py, 7px) var(--rail-row-px, 9px);
  min-width: 0;
  min-height: 0;
  background: transparent;
  border: 1px dashed rgba(198, 167, 94, 0.28);
  border-radius: 8px;
  color: rgba(182, 146, 76, 0.85);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: var(--rail-section-size, 0.7rem);
  font-weight: 500;
  letter-spacing: 0.02em;
  text-align: center;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease;

  &:hover:not(:disabled) {
    background: rgba(198, 167, 94, 0.08);
    border-color: rgba(198, 167, 94, 0.45);
    color: var(--brand-gold);
  }
  &:focus-visible {
    outline: 2px solid rgba(198, 167, 94, 0.6);
    outline-offset: -2px;
  }
  &:disabled {
    cursor: default;
    opacity: 0.6;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
